/**
 * Version detection and update notification system
 *
 * Proactively detects when a new version of the app is deployed
 * and prompts users to refresh before they encounter chunk errors.
 *
 * This prevents the "Failed to fetch dynamically imported module" errors
 * by detecting version mismatches early.
 */

import { toast } from 'sonner';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_STORAGE_KEY = 'app-version-hash';

/**
 * Get the current build version/hash from meta tags or environment
 */
function getCurrentVersion(): string {
  // Check for version in meta tag (can be injected during build)
  const versionMeta = document.querySelector('meta[name="app-version"]');
  if (versionMeta) {
    return versionMeta.getAttribute('content') || '';
  }

  // Fallback to environment variable
  return import.meta.env.VITE_APP_VERSION || 'unknown';
}

/**
 * Check if a new version is available by fetching index.html
 * and comparing build hashes/timestamps
 */
async function checkForNewVersion(): Promise<boolean> {
  try {
    // Fetch the current index.html with cache-busting
    const response = await fetch(`/?_=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-cache',
    });

    // Check ETag or Last-Modified headers
    const etag = response.headers.get('etag');
    const lastModified = response.headers.get('last-modified');

    const currentHash = etag || lastModified || '';
    const storedHash = sessionStorage.getItem(VERSION_STORAGE_KEY);

    // First time checking - store the hash
    if (!storedHash) {
      sessionStorage.setItem(VERSION_STORAGE_KEY, currentHash);
      return false;
    }

    // Compare hashes
    if (currentHash && storedHash && currentHash !== storedHash) {
      return true; // New version detected
    }

    return false;
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.debug('Version check failed:', error);
    return false;
  }
}

/**
 * Prompt the user to refresh when a new version is available
 */
function promptUserToRefresh(): void {
  toast.info('A new version is available', {
    description: 'Please refresh to get the latest updates.',
    duration: Infinity, // Don't auto-dismiss
    action: {
      label: 'Refresh Now',
      onClick: () => {
        window.location.reload();
      },
    },
  });
}

/**
 * Initialize version detection system
 *
 * Call this once when the app starts. It will:
 * 1. Store the current version hash
 * 2. Check for updates periodically
 * 3. Prompt user to refresh when a new version is detected
 */
export function initVersionDetection(): void {
  // Store initial version
  const currentVersion = getCurrentVersion();
  console.debug('App version:', currentVersion);

  // Check for updates periodically
  const intervalId = setInterval(async () => {
    const hasNewVersion = await checkForNewVersion();
    if (hasNewVersion) {
      promptUserToRefresh();
      // Stop checking once we've detected an update
      clearInterval(intervalId);
    }
  }, VERSION_CHECK_INTERVAL);

  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    clearInterval(intervalId);
  });

  // Also check on visibility change (when user returns to tab)
  document.addEventListener('visibilitychange', async () => {
    if (!document.hidden) {
      const hasNewVersion = await checkForNewVersion();
      if (hasNewVersion) {
        promptUserToRefresh();
        clearInterval(intervalId);
      }
    }
  });
}

/**
 * Manually trigger a version check
 * Useful for testing or triggering from UI
 */
export async function checkAppVersion(): Promise<void> {
  const hasNewVersion = await checkForNewVersion();
  if (hasNewVersion) {
    promptUserToRefresh();
  } else {
    toast.success('You have the latest version');
  }
}
